import { SERVICE, requirejsOne } from '@ds/swymkit/tools'
import type { ODEEditor_AMD, ODELoader_AMD, ODEUtils_AMD } from '@ds/swymkit/typings'
import {
  checkIsAndroidOrIphoneDevice,
  checkIsInRichClient,
  checkIsMobileDevice,
  checkIsTouchDevice,
  logger,
} from '@ds/swymkit/utils'
import type { MaybeRef } from '@vueuse/shared'
import type { LocationQuery } from 'vue-router'

import { getAIAssistants } from '~/features-swym/aura/api/ai'
import { AIAssistantName } from '~/features-swym/aura/interfaces/ai-assistants'
import { isOrganizationAvailable } from '~/features-swym/profile/utils/organization-fetch'

import { getPlatformId, isOnPremise } from '~/features-shared/config-manager/stores/swym-config'
import { useTenantStore } from '~/features-shared/config-manager/stores/tenant'
import { isRunningInTest } from '~/features-shared/libs/utils/vitest'

import { getServiceUrl } from '~/features-generic/compass/utils/compass-services'
import type { FeatureManagerConfig } from '~/features-generic/config-manager/interfaces/feature-manager'
import { requestIdleOrTimeoutCallback } from '~/features-generic/libs/utils/request-idle'
import { getUWPAppProperty } from '~/features-generic/uwp/api/uwp'

export type DEFAULT_BEHAVIOR = 'default'

interface IFeatureManagerExport {
  default: FeatureManagerConfig
  SERVICE_DEPENDENT_FEATURE: Record<keyof FeatureManagerConfig, string>
  UWP_DEPENDENT_FEATURE: Record<keyof FeatureManagerConfig, string>
}

const MANAGER_MODULES = import.meta.glob<false, '', IFeatureManagerExport>('~/config/features-manager/*.ts')
const DEFAULT_MANAGER: T_FEATURE_MANAGER_ALLOW_LIST = 'swym-standalone'
const FEATURE_MANAGER_KEY = 'swym:feature-manager'

export type T_FEATURE_MANAGER_ALLOW_LIST =
  'swym-standalone' | 'swym-widget' | 'home-by-me' | 'content-reader' | 'profile-widget' | 'swym-standalone-odt'
  | 'rich-client'

export const FEATURE_MANAGER_ALLOW_LIST: T_FEATURE_MANAGER_ALLOW_LIST[] = [
  'swym-standalone',
  'swym-widget',
  'home-by-me',
  'content-reader',
  'profile-widget',
  'swym-standalone-odt',
  'rich-client',
]
export const FEATURE_MANAGER_NAME = ref<T_FEATURE_MANAGER_ALLOW_LIST>(DEFAULT_MANAGER)

const VERIFIED_FEATURE: Set<keyof FeatureManagerConfig> = new Set()
const SERVICE_DEPENDENT_FEATURE: Partial<Record<keyof FeatureManagerConfig, SERVICE>> = {}
const UWP_DEPENDENT_FEATURE: Partial<Record<keyof FeatureManagerConfig, string>> = {}

const FeatureManager: Partial<FeatureManagerConfig> = reactive({})

/**
 * Check if the value is a boolean
 * @param value string to check
 * @returns boolean
 */
function checkStringIsBoolean(value: string | undefined) {
  return value && /^true|false$/i.test(value)
}

function isFeatureActivated(key: keyof FeatureManagerConfig) {
  return FeatureManager[key]
}

/**
 * Returns the prefixed key to get the feature manager. Uses {@link buildPrefixedKey} method
 *
 * @returns The prefixed key for the feature manager
 */
export async function prefixedFeatureManagerKey() {
  const { buildPrefixedKey } = await import('~/features-generic/config-manager/utils/local-storage-manager')
  return buildPrefixedKey(FEATURE_MANAGER_KEY)
}

/**
 * Toggle feature state. Force the state with the 2nd parameter
 * @param key feature name
 * @param value optional params to force the feature state
 */
export function toggleFeature(key: keyof FeatureManagerConfig, value?: boolean) {
  FeatureManager[key] = (value !== undefined ? value : !FeatureManager[key]) as unknown as undefined
}

/**
 * Check if the given variable is activated from UWP variable
 * @param key feature name
 * @param uwpKey UWP variable name
 * @returns Promise with boolean - true if activated, else false.
 */
export async function checkFeatureActivationFromUWP(key: keyof FeatureManagerConfig, uwpKey: string) {
  const activationValue = await getUWPAppProperty(uwpKey)

  if (checkStringIsBoolean(activationValue)) {
    return activationValue !== 'false'
  }

  else if (activationValue === 'enabled') {
    return true
  }

  else if (activationValue === 'undefined' || !activationValue) {
    return typeof FeatureManager[key] === 'boolean'
      ? FeatureManager[key] as boolean // feature check with this should be boolean only
      : false
  }

  else {
    try {
      // if the current user = user group name, the feature is
      // available for the current user if he is a member of the user group
      const { userGroup, getCurrentUser } = await import('~/entities/user/user/api/users')
      const user = (await getCurrentUser()).result
      return userGroup.isMember(user.uid, activationValue)
    }
    catch {
      return false
    }
  }
}

/**
 * Check if a feature is activated from remote variable (UWP or Service)
 * @param key feature name
 * @returns Promise with boolean
 */
export async function verifyFeatureAvailability<
  T extends keyof FeatureManagerConfig,
>(key: T): Promise<FeatureManagerConfig[T]> {
  if (checkIfFeatureHasBeenVerified(unref(key)))
    return FeatureManager[key] as FeatureManagerConfig[T]

  let uwpResult = true
  let serviceResult = true

  VERIFIED_FEATURE.add(key)

  if (Object.prototype.hasOwnProperty.call(UWP_DEPENDENT_FEATURE, key))
    uwpResult = await checkFeatureActivationFromUWP(key, UWP_DEPENDENT_FEATURE[key] as string)

  if (Object.prototype.hasOwnProperty.call(SERVICE_DEPENDENT_FEATURE, key))
    serviceResult = !!(await getServiceUrl(SERVICE_DEPENDENT_FEATURE[key] as SERVICE))

  const result = uwpResult && serviceResult
  toggleFeature(key, result)

  return result as FeatureManagerConfig[T]
}

/**
 * TODO DOC
 * @param tenantGranted
 */
async function checkODEDoctypeSupport(tenantGranted: boolean) {
  const { Preferences } = await import('@3ds/common-preferences')

  const ODEUtils = await requirejsOne<ODEUtils_AMD>('DS/OfficeDocumentEditorClient/Utils')
  const [
    isWordSupported,
    isSpreadsheetSupported,
    isPresentationSupported,
  ] = [
    ODEUtils.DOCTYPE.WORD,
    ODEUtils.DOCTYPE.SPREADSHEET,
    ODEUtils.DOCTYPE.PRESENTATION,
  ].map((doctype) => {
    return ODEUtils.isDeviceSupportedForEditingDoc(
      Preferences.isNativeApp(),
      doctype,
    )
  })

  toggleFeature('content.features.ode_editor.word', tenantGranted && isWordSupported)
  toggleFeature('content.features.ode_editor.spreadsheet', tenantGranted && isSpreadsheetSupported)
  toggleFeature('content.features.ode_editor.presentation', tenantGranted && isPresentationSupported)
}

export async function checkODEActivation() {
  const { Preferences } = await import('@3ds/common-preferences')
  if (Preferences.isNativeApp()) { // IR-1010928 disable ODE Collab on native app
    toggleFeature('content.features.collab.ode', false)
  }
  requestIdleOrTimeoutCallback(async () => {
    const odeUrl = await getServiceUrl(SERVICE.ODE)

    // Require ODE Loader before ODE
    const ODELoader = await requirejsOne<ODELoader_AMD>
    ('DS/OfficeDocumentEditorClientLoader/OfficeDocumentEditorClientLoader')
    try {
      await ODELoader.enableRemoteLoad() // ou6 -> update requirejs to use ODE service URL when ODE modules are used
    }
    catch { /**/ }

    // Require ODE
    const ODEEditor = await requirejsOne<ODEEditor_AMD>('DS/OfficeDocumentEditorClient/Editor')

    if (ODEEditor && odeUrl) {
      toggleFeature('content.features.ode_display', true)
      try {
        const ODETenantGranted: boolean = await ODEEditor.isGranted({ platformId: getPlatformId() })

        toggleFeature('content.features.ode_granted', ODETenantGranted)

        await checkODEDoctypeSupport(ODETenantGranted)
      }
      catch { /**/ }
    }
  })
}

export async function check3DWorkforceActivation(): Promise<boolean> {
  let organizationUrl = ''
  try {
    organizationUrl = await getServiceUrl(SERVICE.ORGANIZATION)
  }
  catch { /* Do nothing, error will be handled by next line */ }

  if (!organizationUrl) {
    toggleFeature('profile.3dworkforce', false)
    return false
  }

  const wfLoader = await requirejsOne<{ checkAndEnableWorkForce:(opts: { platformId: string }) => unknown }>
    ('DS/ENXWFLoader/core/ENXWFCommonUILoader')

  try {
    const enxModule = await wfLoader.checkAndEnableWorkForce({
      platformId: getPlatformId(),
    })
    if (enxModule)
      toggleFeature('profile.3dworkforce', true)
  }
  catch {
    toggleFeature('profile.3dworkforce', false)
  }
  return isFeatureActivated('profile.3dworkforce') as boolean
}

/**
 * @description function to determine organization is enabled or not
 *
 * @returns if organization structure is available or not
 */
export async function checkOrganizationActivation(): Promise<boolean> {
  if (isRunningInTest())
    return false
  const key = 'profile.organization'
  const result = await checkFeatureActivationFromUWP(key, UWP_DEPENDENT_FEATURE[key] as string)

  if (!result) {
    toggleFeature('profile.organization', false)
    return isFeatureActivated('profile.organization') as boolean
  }

  try {
    await isOrganizationAvailable()
    toggleFeature('profile.organization', true)
  }
  catch {
    toggleFeature('profile.organization', false)
  }
  VERIFIED_FEATURE.add(key)
  return isFeatureActivated('profile.organization') as boolean
}

/**
 * Check that AURA and Ask AURA assistants are available and activate the features.
 *
 * If the UWP variables are set to false, ask the AIAssistantInfra service about the assistants' availability and activate the features accordingly.
 */
export async function checkAIAssistantsActivation(): Promise<void> {
  const isAuraActivated = await verifyFeatureAvailability('ai.aura.enable')
  const isAskAuraActivated = await verifyFeatureAvailability('ai.ask_aura.enable')

  if (!isAuraActivated || !isAskAuraActivated) {
    try {
      const aiAssistants = await getAIAssistants()
      if (aiAssistants.includes(AIAssistantName.AURA))
        toggleFeature('ai.aura.enable', true)
      if (aiAssistants.includes(AIAssistantName.ASK_AURA))
        toggleFeature('ai.ask_aura.enable', true)
    }
    catch { /**/ }
  }
}

/**
 * Check if Conference is activated on tenant side
 *
 * If it's not all conference feature should be put to false
 */
export async function checkConferenceActivation(): Promise<void> {
  const isConferenceActivated = await verifyFeatureAvailability('community.creation.conference')
  const isConferenceInConversationActivated = await verifyFeatureAvailability('conversation.creation.conference')

  if (isConferenceActivated || isConferenceInConversationActivated) {
    try {
      const tenantStore = useTenantStore()
      const tenantOptions = await tenantStore.getOrFetchOptions()

      if (!tenantOptions.config.tenant.conferences_enabled) {
        toggleFeature('community.creation.conference', false)
        toggleFeature('conversation.creation.conference', false)
        // Rituals can only work if we have meeting activated on the tenant so we deactivated it to
        toggleFeature('rituals.enabled', false)
      }
    }
    catch { /**/ }
  }
}

function getManagerModule(managerName: T_FEATURE_MANAGER_ALLOW_LIST): Promise<IFeatureManagerExport> {
  const [,manager] = (Object
    .entries(MANAGER_MODULES)
    .find(([key]) => key.endsWith(`${managerName}.ts`)) || [])

  if (!manager)
    throw new Error(`Manager ${managerName} not found.`)

  return manager()
}

function initFeatureManagerOnPremise() {
  if (!isOnPremise())
    return

  toggleFeature('app.config.is_cloud', false)
  toggleFeature('calls', false)
  toggleFeature('community.features.media_library.source.3ddrive', false)
  toggleFeature('community.features.ga_facet', false)
  toggleFeature('community.features.la_facet', false)
  toggleFeature('community.features.mention_all', false)
  toggleFeature('content.creation.media.sketch', false)
  toggleFeature('content.creation.media.story', false)
  toggleFeature('content.creation.media.whiteboard', false)
  toggleFeature('conversation.features.calls', false)
  toggleFeature('conversation.features.mention_all', false)
  toggleFeature('conversation.rtc', false)
  toggleFeature('search.sources.usersgroup', false)
}

/**
 * Initialize the feature manager
 * @description It will fetch the configuration and then overload with query string values
 * @param managerName Manager name to load
 * @default DEFAULT_MANAGER = 'swym-standalone'
 * @param queries Query string value to overload default values
 */
let IS_MANAGER_INITIALIZED = false
// eslint-disable-next-line max-statements
export async function initFeatureManager(managerName = FEATURE_MANAGER_NAME.value, queries: LocationQuery) {
  // Only init manager if it is in the allow list
  if (!FEATURE_MANAGER_ALLOW_LIST.includes(managerName))
    return

  // Fetch needed manager values
  const {
    default: manager,
    UWP_DEPENDENT_FEATURE: managerUwpVar,
    SERVICE_DEPENDENT_FEATURE: managerServiceVar,
  } = await getManagerModule(managerName)

  // Assign manager values to the reactive object
  Object.assign(FeatureManager, manager)
  Object.assign(UWP_DEPENDENT_FEATURE, managerUwpVar)
  Object.assign(SERVICE_DEPENDENT_FEATURE, managerServiceVar)

  Object.keys(queries)
    .forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(FeatureManager, key)) {
      // parse as boolean if it is a boolean or just return the string
        const value = checkStringIsBoolean(queries[key] as string)
          ? queries[key] !== 'false'
          : queries[key]
        // Override manager value with the value from the query string
        FeatureManager[key as keyof FeatureManagerConfig] = value as unknown as undefined
      }
    })

  FEATURE_MANAGER_NAME.value = managerName

  toggleFeature('app.config.is_in_rich_client', checkIsInRichClient())
  toggleFeature('app.config.mobile_device', checkIsMobileDevice())
  toggleFeature('app.config.touch_device', checkIsTouchDevice())
  toggleFeature('content.creation.media.story', !checkIsAndroidOrIphoneDevice())

  checkConferenceActivation()

  checkAIAssistantsActivation()
  requestIdleOrTimeoutCallback(() => {
    checkODEActivation()

    verifyFeatureAvailability('app.config.is_cloud')
  })

  initFeatureManagerOnPremise()

  // Persist the manager if it is not default to use it on refresh
  if (managerName === DEFAULT_MANAGER)
    sessionStorage.removeItem(await prefixedFeatureManagerKey())
  else
    sessionStorage.setItem(await prefixedFeatureManagerKey(), managerName)

  IS_MANAGER_INITIALIZED = true
}

/**
 * Check if we already verified the variable is activated remotely
 * @param key feature name
 * @returns true if the feature has been already verified
 */
function checkIfFeatureHasBeenVerified(key: keyof FeatureManagerConfig) {
  if (
    Object.prototype.hasOwnProperty.call(UWP_DEPENDENT_FEATURE, key)
    || Object.prototype.hasOwnProperty.call(SERVICE_DEPENDENT_FEATURE, key)
  ) {
    // If feature needs to be verified on remote variables, check
    return VERIFIED_FEATURE.has(key)
  }

  // variable do NOT need to be verified. returns true
  return true
}

/**
 * Check a feature is activated or not
 * @param key of the FeatureManagerConfig keys list
 * @returns Computed with value or boolean. Give true by default
 */
export function useFeatureManager<
  T extends keyof FeatureManagerConfig,
>(key: MaybeRef<T>): ComputedRef<FeatureManagerConfig[T]> {
  // This error will prevent the feature manager to be used before being initialized.
  if (!IS_MANAGER_INITIALIZED)
    logger.error(new Error('FEATURE MANAGER IS NOT INITIALIZED YET'))

  // When a feature variable is used
  // Verify automatically if it is activated from remote variable
  // if it has not been verified already
  verifyFeatureAvailability(unref(key))

  // If the feature is not set yet, set it to true. If the feature is not set
  // before the computed initialization, the computed will not be reactive
  if (!Object.prototype.hasOwnProperty.call(FeatureManager, unref(key)))
    FeatureManager[unref(key)] = true as FeatureManagerConfig[T]

  return computed(() => FeatureManager[unref(key)] as FeatureManagerConfig[T])
}

if (import.meta.env.DEV) {
  // @ts-expect-error Helper for DEV mode only.
  window.swym_use_feature_manager = useFeatureManager
  // @ts-expect-error Helper for DEV mode only.
  window.swym_features_manager_toggle = toggleFeature
}
